if (!customElements.get('predictive-search')) {
  /**
   * PredictiveSearch
   * @description Uses the Shopify predictive search api for rendering search results.
   * @link https://shopify.dev/api/ajax/reference/predictive-search
   */
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();

      // Options
      this.options = {
        hiddenClass: 'hidden',
      };

      // Get options from element data and combine with this.options
      if (this?.dataset?.options) {
        const dataOptions = JSON.parse(this.dataset.options);
        this.options = {
          ...this.options,
          ...dataOptions,
        };
      }

      this.init();
    }

    /**
     * init
     * @description Select elements and add events.
     */
    init() {
      this.input = this.querySelector('[data-predicative-search="input"]');
      this.predictiveSearchResults = this.querySelector('[data-predicative-search="results"]');
      this.predictiveSearchResources =
        this.predictiveSearchResults.getAttribute('data-resources-types') || 'product';
      this.predictiveSearchMaxSize =
        Number.parseInt(this.predictiveSearchResults.getAttribute('data-max-results'), 10) || 8;

      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 100).bind(this),
      );

      this.handleInputState();

      // Handle typing
      this.input.addEventListener('keyup', () => {
        this.handleInputState();
      });
    }

    /**
     * onChange
     * @description Checks the input value and will call the fetch data function
     * @returns {Void}
     */
    onChange() {
      const searchTerm = this.input.value.trim();
      if (!searchTerm.length) {
        this.close();
        return;
      }
      this.getSearchResults(searchTerm);
    }

    /**
     * getSearchResults
     * @description Fetch data from the predictive search api.
     * @param {String} searchTerm
     */
    getSearchResults(searchTerm) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      const predictiveSearchParams = new URLSearchParams({
        q: searchTerm,
        'resources[type]': this.predictiveSearchResources,
        'resources[limit]': this.predictiveSearchMaxSize,
        'resources[limit_scope]': 'each',
        'resources[options][fields]': 'title,product_type',
        section_id: 'predictive-search',
      });
      const searchCountParams = new URLSearchParams({
        q: searchTerm,
        type: 'product',
        'options[prefix]': 'last',
        section_id: 'predictive-search-count',
      });

      const predictiveSearchRequest = fetch(
        `${routes.predictive_search_url}?${predictiveSearchParams.toString()}`,
        { signal: this.abortController.signal },
      );
      const searchCountRequest = fetch(
        `${routes.search_url || '/search'}?${searchCountParams.toString()}`,
        { signal: this.abortController.signal },
      ).catch(() => null);

      Promise.all([predictiveSearchRequest, searchCountRequest])
        .then(async ([predictiveSearchResponse, searchCountResponse]) => {
          if (!predictiveSearchResponse.ok) {
            var error = new Error(predictiveSearchResponse.status);
            this.close();
            throw error;
          }

          return Promise.all([
            predictiveSearchResponse.text(),
            searchCountResponse?.ok ? searchCountResponse.text() : null,
          ]);
        })
        .then(([predictiveSearchText, searchCountText]) => {
          const resultsMarkup = new DOMParser()
            .parseFromString(predictiveSearchText, 'text/html')
            .querySelector('#shopify-section-predictive-search').innerHTML;
          this.predictiveSearchResults.innerHTML = resultsMarkup;

          if (searchCountText) {
            const searchCountMarkup = new DOMParser().parseFromString(searchCountText, 'text/html');
            const totalResults =
              Number.parseInt(
                searchCountMarkup
                  .querySelector('[data-predictive-search-total-count]')
                  ?.getAttribute('data-predictive-search-total-count'),
                10,
              ) || 0;
            const displayedResults = this.predictiveSearchResults.querySelectorAll(
              '[data-predictive-search-product]',
            ).length;
            const seeAllLink = this.predictiveSearchResults.querySelector(
              '[data-predictive-search-see-all]',
            );
            const countLabel = this.predictiveSearchResults.querySelector(
              '[data-predictive-search-count-label]',
            );

            if (countLabel) {
              countLabel.textContent = countLabel
                .getAttribute('data-count-template')
                .replace('[displayed]', displayedResults)
                .replace('[total]', totalResults);
            }

            if (seeAllLink && totalResults > displayedResults) {
              seeAllLink.classList.remove(this.options.hiddenClass);
            }
          }

          this.open();
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          this.close();
          throw error;
        });
    }

    /**
     * open
     * @description Open the predictive search results.
     */
    open() {
      this.predictiveSearchResults.style.display = 'block';
    }

    /**
     * close
     * @description Close the predictive search results.
     */
    close() {
      this.predictiveSearchResults.style.display = 'none';
    }

    /**
     * handleInputState
     * @description Checks if the input has a value and toggles the input reset button.
     */
    handleInputState() {
      const state = this.input.value.length > 0 ? true : false;
      if (!state) {
        this.close();
      }
      return state;
    }

    /**
     * clearInput
     * @description Clears the input.
     */
    clearInput() {
      this.input.value = '';
      this.input.focus();
    }
  }

  window.PredictiveSearch = PredictiveSearch;

  customElements.define('predictive-search', PredictiveSearch);
}
